import { ArrowIcon, CheckCircleIcon } from "asset/icons";
import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import { countrycodelist } from "constants/countryList";
import { Fragment, useEffect, useRef, useState, useMemo } from "react";
import { Bars } from "react-loader-spinner";
import { useSelector } from "react-redux";
import capitalizeWords from "utilities/capitalizeWords";
import getInitials from "utilities/getInitials";

const normalizeCurrency = (cur) => {
    if (!cur) return "NGN";
    if (typeof cur === "string") return cur.toUpperCase();
    if (typeof cur === "object") {
        const guess =
            cur.code || cur.value || cur.currency || cur.symbol || cur?.current || cur?.active;
        return String(guess || "NGN").toUpperCase();
    }
    return "NGN";
};

const BankSelect = ({
    fetchNGNBanks,
    NGNBanks: _NGNBanks,
    NGNBanksLoading,
    bankName,
    setBank,
    close,
    country,
    setCountry,
}) => {
    const dropDownRef = useRef();
    const [searchString, setSearchString] = useState("");

    const { currency: rawCurrency } = useSelector((s) => s);
    const selectedCurrency = useMemo(() => normalizeCurrency(rawCurrency), [rawCurrency]);

    const isListCurrency = selectedCurrency === "NGN" || selectedCurrency === "KES";
    const isKenya = selectedCurrency === "KES";
    const isNigeria = selectedCurrency === "NGN";

    const banks = (_NGNBanks || []).filter(({ name }) =>
        name?.toLowerCase?.().includes(searchString.toLowerCase())
    );
    const alphabets = [...new Set(banks.map(({ name }) => name?.charAt(0)?.toUpperCase()))]
        .filter(Boolean)
        .sort();

    useEffect(() => {
        if (!isListCurrency) return;
        if (!_NGNBanks || _NGNBanks.length === 0) {
            fetchNGNBanks(
                `/v1/wallets/get_all_banks/all?currencyFrom=${selectedCurrency}&currencyTo=${selectedCurrency}`
            );
        }
    }, [isListCurrency, selectedCurrency, _NGNBanks, fetchNGNBanks]);

    return (
        <div className="relative bg-page-bg rounded-xl w-full !p-4 z-30 shadow-[0px_8px_5px_0px_#00000014,0px_20px_13px_0px_#00000008]">
            <p
                className="bg-white rounded-lg h-12 !px-2 font-[450] text-main-dark flex items-center cursor-pointer relative z-20"
                onClick={() => dropDownRef?.current?.click()}
            >
                <img
                    src={country.image}
                    alt={country.title}
                    className="size-8 rounded-full !mr-2"
                />
                {country.title}
            </p>

            <Dropdown
                ref={dropDownRef}
                options={countrycodelist.map(({ image, title, ...rest }, idx) => ({
                    label: (
                        <p
                            className={`font-[450] text-main-dark w-full flex items-center cursor-pointer pointer-events-auto !px-4 !py-2.5 ${
                                idx && "!mt-1.5"
                            }`}
                        >
                            <img
                                src={image}
                                alt={title}
                                className="size-[26px] rounded-full !mr-2"
                            />
                            {title}
                            {country.title === title && <CheckCircleIcon className="ml-auto" />}
                        </p>
                    ),
                    value: title,
                    action: () => {
                        if (country.title !== title) {
                            setCountry({ title, image, ...rest });
                            setBank(null, null);
                        }
                    },
                    className: "!p-0",
                }))}
                className="!h-12 -translate-y-12 z-10 !pointer-events-none"
            />

            <>
                {isListCurrency ? (
                    <>
                        <Input
                            onChange={(v) => setSearchString(v)}
                            value={searchString}
                            placeholder={`Search ${
                                selectedCurrency === "KES" ? "Kenyan" : "Nigerian"
                            } banks`}
                            className="-translate-y-12 !shadow-[inset_0_0_0_1000px_#EAECF0]"
                        />

                        {/* Small helper for KES account-name rule */}
                        {/* {isKenya && (
                            <p className="text-xs text-[#667085] -mt-10 mb-2">
                                Tip: For KES, you’ll still enter the <strong>Account name</strong>{" "}
                                manually.
                            </p>
                        )} */}

                        <div className="mt-3 -translate-y-12 rounded-lg max-h-[280px] -mb-11 overflow-y-auto">
                            {NGNBanksLoading ? (
                                <div className="grid h-56 place-items-center">
                                    <Bars
                                        height="22"
                                        width="22"
                                        color="#6f00ff"
                                        ariaLabel="bars-loading"
                                        wrapperClass="w-max"
                                    />
                                </div>
                            ) : (
                                <>
                                    {alphabets.map((letter, idx) => (
                                        <Fragment key={letter}>
                                            <p
                                                className={`font-medium text-left text-xs text-[#667085] ${
                                                    idx && "!mt-[15px]"
                                                } !mb-2`}
                                            >
                                                {letter}
                                            </p>
                                            <div className="rounded-lg !px-2 bg-white flex flex-col gap-2.5 !py-2.5">
                                                {banks
                                                    .filter(({ name }) =>
                                                        name?.toUpperCase?.().startsWith(letter)
                                                    )
                                                    .map(({ name, code }) => (
                                                        <div
                                                            className="flex items-center gap-2 !py-1.5 cursor-pointer rounded-lg"
                                                            onClick={() => {
                                                                // 3rd arg is optional, parent can ignore it safely
                                                                setBank(name, code, {
                                                                    requiresManualAccountName:
                                                                        isKenya,
                                                                });
                                                                close();
                                                            }}
                                                            key={name + code}
                                                        >
                                                            <span
                                                                className={`!size-10 bg-[#F6F6F6] rounded-full text-[#344054] font-[450] grid place-items-center overflow-hidden ${
                                                                    getInitials(name).length > 3 &&
                                                                    "text-[13px]"
                                                                }`}
                                                            >
                                                                {getInitials(name)}
                                                            </span>
                                                            <p className="text-main-dark font-[450]">
                                                                {capitalizeWords(name)}
                                                            </p>
                                                            {bankName === name && (
                                                                <CheckCircleIcon className="ml-auto !mr-2 !size-5" />
                                                            )}
                                                        </div>
                                                    ))}
                                            </div>
                                        </Fragment>
                                    ))}
                                    {alphabets.length === 0 && (
                                        <p className="text-sm text-[#6F739B] px-2 py-3 bg-white rounded-lg">
                                            No banks found for “{searchString}”.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <p className="font-medium text-sm text-left -translate-y-[30px] text-[#475467]">
                            Bank name
                        </p>
                        <Input
                            onChange={(value) => setBank(value, null)}
                            value={bankName}
                            placeholder="Enter bank name"
                            className="-translate-y-12 !shadow-[inset_0_0_0_1000px_#EAECF0]"
                        />
                        <button
                            className={`${
                                bankName
                                    ? "!bg-secondary pointer-events-auto"
                                    : "!bg-[#D9CDFF] pointer-events-none"
                            } text-white cursor-pointer -translate-y-7 rounded-lg !py-2 px-7 mr-auto !mb-[134px]`}
                            onClick={close}
                        >
                            Ok
                        </button>
                    </>
                )}
            </>
        </div>
    );
};

export default BankSelect;
